import { useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useAuth } from "../context/AuthContext";
import { formatApiError } from "../lib/api";
import LanguageSwitcher from "../components/LanguageSwitcher";
import { ArrowRight, Loader2 } from "lucide-react";

export default function Login() {
  const { t } = useTranslation();
  const { login } = useAuth();
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [err, setErr] = useState("");
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setErr("");
    setBusy(true);
    try {
      const u = await login(email, password);
      navigate(u.role === "pharmacy" ? "/dashboard" : "/missions");
    } catch (e) {
      setErr(formatApiError(e.response?.data?.detail) || e.message);
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="min-h-screen flex">
      <div className="hidden lg:flex w-1/2 bg-[#1A4D2E] relative overflow-hidden">
        <div className="absolute inset-0 rp-grain opacity-30" />
        <div className="relative z-10 p-12 flex flex-col justify-between text-white">
          <div className="flex items-center gap-3">
            <img src="/apocia-logo.png" alt="Apocia" className="h-12 w-auto" style={{ filter: "brightness(0) invert(1)" }} data-testid="login-logo" />
          </div>
          <div className="space-y-4 max-w-md">
            <h1 className="text-5xl font-medium leading-tight" style={{ fontFamily: "Outfit" }}>
              {t("auth.tagline")}
            </h1>
            <p className="text-white/70 text-base leading-relaxed">
              {t("auth.tagline_sub")}
            </p>
          </div>
          <div className="text-white/50 text-xs">
            © {new Date().getFullYear()} Apocia — La relève des pharmaciens
          </div>
        </div>
      </div>
      <div className="flex-1 grid place-items-center p-8 bg-[#F7F8F6] relative">
        <div className="absolute top-6 right-6">
          <LanguageSwitcher />
        </div>
        <form
          onSubmit={submit}
          className="w-full max-w-md bg-white rounded-3xl border border-[#E2E5E0] p-10 shadow-sm"
          data-testid="login-form"
        >
          <h2 className="text-3xl font-medium tracking-tight" style={{ fontFamily: "Outfit" }}>
            {t("auth.hello")} 👋
          </h2>
          <p className="text-sm text-[#6B726A] mt-2 mb-8">
            {t("auth.welcome_subtitle")}
          </p>

          <label className="text-xs uppercase tracking-wider font-semibold text-[#6B726A]">{t("auth.email")}</label>
          <input
            data-testid="login-email-input"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            type="email"
            required
            className="w-full mt-2 mb-5 bg-white border border-[#E2E5E0] rounded-xl px-4 py-3 focus:ring-2 focus:ring-[#1A4D2E]/20 focus:border-[#1A4D2E] outline-none"
            placeholder="vous@officine.fr"
          />

          <label className="text-xs uppercase tracking-wider font-semibold text-[#6B726A]">{t("auth.password")}</label>
          <input
            data-testid="login-password-input"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            type="password"
            required
            minLength={6}
            className="w-full mt-2 mb-3 bg-white border border-[#E2E5E0] rounded-xl px-4 py-3 focus:ring-2 focus:ring-[#1A4D2E]/20 focus:border-[#1A4D2E] outline-none"
            placeholder="••••••••"
          />

          {err && (
            <div data-testid="login-error" className="text-sm text-[#E3655B] bg-[#FFF4F3] border border-[#E3655B]/20 rounded-xl px-3 py-2 mb-3">
              {err}
            </div>
          )}

          <button
            data-testid="login-submit-button"
            disabled={busy}
            className="rp-btn-primary w-full flex items-center justify-center gap-2 disabled:opacity-60"
          >
            {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <ArrowRight className="w-4 h-4" />}
            {t("auth.login")}
          </button>

          <p className="text-xs text-center mt-3">
            <Link to="/forgot-password" className="text-[#6B726A] hover:text-[#1A4D2E] hover:underline" data-testid="forgot-password-link">
              {t("auth.forgot_password")}
            </Link>
          </p>

          <p className="text-sm text-[#6B726A] mt-6 text-center">
            {t("auth.register_cta")}{" "}
            <Link to="/register" className="text-[#1A4D2E] font-medium underline-offset-2 hover:underline" data-testid="register-link">
              {t("auth.register")}
            </Link>
          </p>
        </form>
      </div>
    </div>
  );
}
